import React, { createContext, ReactNode, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { ConfigurationIcon } from 'assets/icons';
import { config } from 'config';
import { AttachmentSentStatusPill } from 'core/attachments/components/AttachmentSentStatusPill';
import { DeleteAttachmentButton } from 'core/attachments/components/DeleteAttachmentButton';
import { DownloadAttachmentFileButton } from 'core/attachments/components/DownloadAttachmentFileButton';
import { EAttachmentType, TAttachment } from 'core/attachments/types';
import { TFunction } from 'i18next';
import { getContainsFilterMeta, getDateRangeFilterMeta, useTableState } from 'modules/table';
import { Button, Typography } from 'modules/ui';
import { tableSortingFilteringPagination } from 'utils';
import { timestamp } from 'utils/formatTimestamp';
import { translateMimeType } from 'utils/translateMimeType';

import { TTransformedAttachment } from '../types';

import styles from 'core/attachments/components/AttachmentsTable/AttachmentsTable.module.scss';

export const ATTACHMENTS_TABLE_COLUMNS = [
    'name',
    'commission',
    'uploadedBy',
    'mimeType',
    'type',
    'tsAdded',
    'actions',
] as const;

export interface IAttachmentsTableContext {
    tableState: ReturnType<typeof useTableState<TAttachment>>;
    columns: ReturnType<typeof attachmentsTableColumnsDefinitions>;
    commissionId: number | null;
    invoiceId: number | null;
    attachments: TAttachment[];
    filteredAttachments: TTransformedAttachment[];

    setCommissionId: (id: number) => void;
    setInvoiceId: (id: number) => void;
    setAttachments: React.Dispatch<React.SetStateAction<TAttachment[]>>;
    setColumnsVisibility: React.Dispatch<
        React.SetStateAction<Partial<Record<(typeof ATTACHMENTS_TABLE_COLUMNS)[number], boolean>>>
    >;
    setHideHeaderActions: React.Dispatch<React.SetStateAction<boolean>>;
    setHideInvoiceState: React.Dispatch<React.SetStateAction<boolean>>;
    setTableName: React.Dispatch<React.SetStateAction<string>>;
}

const AttachmentsTableContext = createContext<IAttachmentsTableContext>({
    tableState: undefined!,
    columns: [],
    commissionId: null,
    invoiceId: null,
    attachments: [],
    filteredAttachments: [],

    setCommissionId: () => null,
    setInvoiceId: () => null,
    setColumnsVisibility: () => null,
    setAttachments: () => null,
    setHideHeaderActions: () => null,
    setHideInvoiceState: () => null,
    setTableName: () => null,
});
export interface IAttachmentsTableContextProviderProps {
    children: ReactNode;
}
export const AttachmentsTableContextProvider: React.FC<IAttachmentsTableContextProviderProps> = ({ children }) => {
    const { t } = useTranslation();
    const [commissionId, setCommissionId] = useState<number | null>(null);
    const [attachments, setAttachments] = useState<TAttachment[]>([]);
    const [invoiceId, setInvoiceId] = useState<number | null>(null);
    const [hideHeaderActions, setHideHeaderActions] = useState<boolean>(false);
    const [hideInvoiceState, setHideInvoiceState] = useState<boolean>(false);
    const [tableName, setTableName] = useState<string>('attachmentsTable');
    const [columnsVisibility, setColumnsVisibility] = useState<
        Partial<Record<(typeof ATTACHMENTS_TABLE_COLUMNS)[number], boolean>>
    >({});

    const columnDefinitions = useMemo(
        () => attachmentsTableColumnsDefinitions(t, hideHeaderActions, hideInvoiceState),
        [hideHeaderActions, hideInvoiceState],
    );
    columnDefinitions.forEach((column) => {
        column.id;
    });
    const attachmentsTableState = useTableState<TAttachment>({
        rowIdKey: 'attachment_id',
        tableName: tableName,
        columnsVisibilityStates: {
            ...columnDefinitions.reduce<Record<string, boolean>>((acc, column) => {
                if (column.id) {
                    acc[column.id] = column.meta?.visible ?? true;
                }
                return acc;
            }, {}),
            ...columnsVisibility,
        },
    });

    const transformedDataForFiltering: TTransformedAttachment[] = useMemo(() => {
        if (!attachments) {
            return [];
        }
        return attachments.map((attachment) => {
            return {
                ...attachment,
                uploadedBy: attachment.uploadedBy
                    ? `${attachment.uploadedBy.name} ${attachment.uploadedBy.surname}`
                    : t('attachments.externalCarrier'),
                typeTrans: t(`attachments.form.fields.attachmentType.${attachment.type}`),
                commission: attachment.commission ? String(attachment.commission.number) : '',
            };
        });
    }, [attachments]);

    const { result: filteredAttachments } = tableSortingFilteringPagination<TTransformedAttachment>({
        data: transformedDataForFiltering,
        queryParams: attachmentsTableState.queryParams,
        paginationParams: attachmentsTableState.paginationParams,
        treatAsDate: ['tsAdded'],
    });

    return (
        <AttachmentsTableContext.Provider
            value={{
                invoiceId,
                setInvoiceId,
                commissionId,
                setTableName,
                setAttachments,
                setCommissionId,
                setHideHeaderActions,
                setColumnsVisibility,
                setHideInvoiceState,
                columns: columnDefinitions,
                attachments: attachments || [],
                filteredAttachments: filteredAttachments,
                tableState: attachmentsTableState,
            }}
        >
            {children}
        </AttachmentsTableContext.Provider>
    );
};

export const useAttachmentsTable = (tableName?: string) => {
    const context = React.useContext(AttachmentsTableContext);
    useLayoutEffect(() => {
        if (tableName) {
            context.setTableName(tableName);
        }
    }, [tableName, context]);
    return context;
};

export const attachmentsTableColumnsDefinitions = (
    t: TFunction,
    hideHeaderActions = false,
    hideInvoiceState = false,
) => {
    const columnHelper = createColumnHelper<TTransformedAttachment>();
    const columns = [
        columnHelper.accessor('name', {
            id: 'name',
            header: t('attachments.table.columns.name'),
            meta: {
                ...(!hideHeaderActions ? getContainsFilterMeta() : {}),
                cellStyle: {},
            },
            enableSorting: !hideHeaderActions,
            cell: (info) => (
                <div className={styles.nameColumnWrapper}>
                    <Typography variant="p" fontWeight="semibold" className={styles.nameColumnText}>
                        {info.getValue()}
                    </Typography>
                    {!hideInvoiceState && info.row.original.type === EAttachmentType.INVOICE && (
                        <AttachmentSentStatusPill sent={info.row.original.sent} size="small" />
                    )}
                </div>
            ),
        }),
        columnHelper.accessor('commission', {
            id: 'commission',
            header: t('attachments.table.columns.commission'),
            enableSorting: !hideHeaderActions,
            cell: (info) => {
                const commissionId = info.row.original.commission_id;
                const commissionNumber = info.getValue();
                if (!commissionId) {
                    return (
                        <Typography variant="p" fontWeight="normal">
                            {'-'}
                        </Typography>
                    );
                }
                return (
                    <Link to={config.routes.commissions.detail.replace(':id', String(commissionId))}>
                        <Typography color="primary" variant="p" fontWeight="semibold">
                            {commissionNumber || '-'}
                        </Typography>
                    </Link>
                );
            },
            meta: {
                ...(!hideHeaderActions ? getContainsFilterMeta() : {}),
                disableRowClick: true,
            },
        }),
        columnHelper.accessor('mimeType', {
            id: 'mimeType',
            header: t('attachments.table.columns.mimeType'),
            enableSorting: !hideHeaderActions,
            size: 50,
            cell: (info) => {
                const mimeType = info.getValue();
                return (
                    <Typography variant="p" fontWeight="normal">
                        {translateMimeType(mimeType)}
                    </Typography>
                );
            },
            meta: {
                ...(!hideHeaderActions ? getContainsFilterMeta() : {}),
            },
        }),
        columnHelper.accessor('typeTrans', {
            id: 'typeTrans',
            header: t('attachments.table.columns.type'),
            enableSorting: !hideHeaderActions,
            cell: (info) => {
                const type = info.getValue();
                return (
                    <Typography variant="p" fontWeight="normal">
                        {type}
                    </Typography>
                );
            },
            meta: {
                ...(!hideHeaderActions ? getContainsFilterMeta() : {}),
            },
        }),
        columnHelper.accessor('uploadedBy', {
            id: 'uploadedBy',
            header: t('attachments.table.columns.uploadedBy'),
            enableSorting: !hideHeaderActions,
            cell: (info) => {
                const name = info.getValue();
                return (
                    <Typography variant="p" fontWeight="normal">
                        {name}
                    </Typography>
                );
            },
            meta: {
                ...(!hideHeaderActions ? getContainsFilterMeta() : {}),
            },
        }),
        columnHelper.accessor('tsAdded', {
            id: 'tsAdded',
            header: t('attachments.table.columns.uploadDate'),
            sortingFn: 'datetime',
            enableSorting: !hideHeaderActions,
            cell: (info) => {
                const date = timestamp(Number(info.getValue()));
                return (
                    <Typography variant="p" fontWeight="normal">
                        {date?.format('DD. MM. YYYY HH:mm')}
                    </Typography>
                );
            },
            meta: {
                ...(!hideHeaderActions ? getDateRangeFilterMeta() : {}),
            },
        }),

        // Static column for actions
        columnHelper.display({
            id: 'actions',
            header: t('attachments.table.columns.actions'),
            enableSorting: !hideHeaderActions,
            cell: (info) => (
                <div className={styles.actionButtonsWrapper}>
                    <Link to={config.routes.attachments.detail.replace(':id', String(info.row.original.attachment_id))}>
                        <Button variant="secondary" Icon={<ConfigurationIcon />} size="small" />
                    </Link>
                    <DownloadAttachmentFileButton
                        attachmentId={info.row.original.attachment_id}
                        hideText
                        size="small"
                    />
                    <DeleteAttachmentButton
                        attachmentId={info.row.original.attachment_id}
                        size="small"
                        redirectOnSuccess={false}
                    />
                </div>
            ),
        }),
    ];
    return columns;
};
